import React, { useEffect, useRef } from "react";
import styles from "./SpinAnimation.module.scss";

type Props = {
  images: string[];          // orbiting icons
  centerSrc?: string;        // center icon (optional)
  radius?: number;           // px
  itemSize?: number;         // px (square)
  centerSize?: number;       // px (square)
  speed?: number;            // seconds per full revolution
  clockwise?: boolean;       // direction
};

function SpinAnimation({
  images,
  centerSrc,
  radius = 130,
  itemSize = 48,
  centerSize = 80,
  speed = 12,
  clockwise = true,
}: Props) {
  const itemsRef = useRef<Array<HTMLDivElement | null>>([]);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  // ensure array length matches images
  itemsRef.current.length = images.length;

  useEffect(() => {
    const count = images.length;
    const baseAngles = new Array(count).fill(0).map((_, i) => (i / count) * 360);
    startRef.current = performance.now();

    // initialize transforms immediately (avoid jump)
    baseAngles.forEach((base, i) => {
      const el = itemsRef.current[i];
      if (el) {
        const angle = base;
        el.style.transform = `translate(-50%,-50%) rotate(${angle}deg) translateX(${radius}px) rotate(${-angle}deg)`;
      }
    });

    const animate = (now: number) => {
      if (!startRef.current) startRef.current = now;
      const elapsed = (now - startRef.current) / 1000; // seconds
      // normalized progress 0..1
      const progress = (elapsed / Math.max(0.0001, speed)) % 1;
      // angle offset in degrees for this time
      const dir = clockwise ? 1 : -1;
      const offset = progress * 360 * dir;

      baseAngles.forEach((base, i) => {
        const el = itemsRef.current[i];
        if (!el) return;
        const angle = base + offset;
        // Order is important:
        // 1) translate(-50%,-50%) centers the element at the center point (so radius doesn't vary)
        // 2) rotate(angle) moves the child along the rotated x-axis
        // 3) translateX(radius) sets exact distance
        // 4) rotate(-angle) cancels the orientation so img stays upright
        el.style.transform = `translate(-50%,-50%) rotate(${angle}deg) translateX(${radius}px) rotate(${-angle}deg)`;
      });

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [images, radius, speed, clockwise]);

  return (
    <div
      className={styles.orbitRoot}
      style={
        {
          ["--radius" as any]: `${radius}px`,
          ["--item-size" as any]: `${itemSize}px`,
          ["--center-size" as any]: `${centerSize}px`,
        } as React.CSSProperties
      }
    >
      {centerSrc && (
        <div className={styles.center} style={{ width: centerSize, height: centerSize }}>
          <img src={centerSrc} alt="center" />
        </div>
      )}

      {images.map((src, i) => (
        <div
          key={i}
          ref={(el) => (itemsRef.current[i] = el)}
          className={styles.orbitItem}
          style={{ width: itemSize, height: itemSize }}
        >
          <img src={src} alt={`orbit-${i}`} draggable={false} />
        </div>
      ))}
    </div>
  );
}


export default React.memo(SpinAnimation);